import { i18nNsFactory } from '@atlas-frontend/i18n';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import { TextLink } from '../../common/components/typography';
import { Table } from '../../common/tanstacktable';
import { FC, memo, useMemo } from 'react';
import {
  suppressHref,
  useHrefWithQueryParams,
} from '../../common/useHrefWithQueryParams';
import { usePanelParams } from '../usePanelParams';
import { sortPeriod } from '../../common/utils';
import { SystemClassifierItem } from '../../systemClassifier/SystemClassifierItem';
import { useDataObjectContext } from '../DataObjectContext';
import { ValidationRuleValidDataType } from '../schema';
import translations from '../translations/ValidationRulesViewTable.translations.json';
import { useTableFilter } from '../useTableFilter';
import { ValidationTableWrapper, useValidationRuleOptions } from './utils';
import { ValidationErrorLevel } from './ValidationErrorLevel';
import { ValidationRuleViewPanel } from './ValidationRuleViewPanel';
import { ValidationStatus } from './ValidationStatus';
import { getValidPeriodDisplayValueWithFormValueAttached } from '../../common/form';

const columnHelper = createColumnHelper<ValidationRuleValidDataType>();

const { addTranslations, useTranslation } = i18nNsFactory(
  'validation-rules-view-table',
);
addTranslations(translations);

export interface ValidationRulesViewTableProps {
  validationRules: ValidationRuleValidDataType[];
}

export function ValidationRulesViewTable({
  validationRules,
}: ValidationRulesViewTableProps) {
  const { t } = useTranslation();
  const options = useValidationRuleOptions();
  const { setFilters } = useDataObjectContext();
  const { columnFilters, setColumnFilters } = useTableFilter(setFilters);

  const columns = useMemo(
    () => [
      columnHelper.accessor('code', {
        header: t('column.code.label'),
        cell: (props) => <CodeCell {...props} />,
        meta: { width: '25ch', className: 'sticky-left' },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('groupCode', {
        header: t('column.groupCode.label'),
        meta: { width: '15ch' },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('errorLevelId', {
        header: t('column.errorLevelId.label'),
        cell: memo((props: CellContext<ValidationRuleValidDataType, any>) => (
          <ValidationErrorLevel itemId={props.row.original.errorLevelId} />
        )),
        meta: { width: 120 },
        filterFn: 'includesOption',
      }),
      columnHelper.accessor('condition', {
        header: t('column.condition.label'),
        meta: { width: '20%', minWidth: 150 },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('expressionLeft', {
        header: t('column.expressionLeft.label'),
        meta: { width: '25%', minWidth: 150 },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('expressionOperatorId', {
        header: t('column.expressionOperatorId.label'),
        cell: (props) => (
          <SystemClassifierItem
            code="VALIDATION_RULE_OPERATOR"
            itemId={props.row.original.expressionOperatorId}
          />
        ),
        meta: { width: 125 },
        filterFn: 'includesOption',
      }),
      columnHelper.accessor('expressionRight', {
        header: t('column.expressionRight.label'),
        meta: { width: '25%', minWidth: 150 },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('tolerance', {
        header: t('column.tolerance.label'),
        meta: { width: 120 },
      }),
      columnHelper.accessor('ignoreError', {
        header: t('column.ignoreError.label'),
        cell: memo((props: CellContext<ValidationRuleValidDataType, any>) =>
          t(`column.ignoreError.${props.renderValue() ? 'yes' : 'no'}`),
        ),
        meta: { width: 120 },
        filterFn: 'includesOption',
      }),
      columnHelper.accessor('ignoreReason', {
        header: t('column.ignoreReason.label'),
        meta: { width: '15%', minWidth: 250 },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('errorDescription', {
        header: t('column.errorDescription.label'),
        meta: { width: '15%', minWidth: 330 },
        filterFn: 'includesString',
      }),
      columnHelper.accessor('statusId', {
        header: t('column.statusId.label'),
        cell: (props) => (
          <ValidationStatus itemId={props.row.original.statusId} />
        ),
        meta: { width: 100 },
        filterFn: 'includesOption',
      }),
      columnHelper.accessor(
        (rowData) =>
          getValidPeriodDisplayValueWithFormValueAttached(rowData.validPeriod),
        {
          id: 'validPeriod',
          header: t('column.validPeriod.label'),
          meta: { width: 200 },
          sortingFn: (a, b) =>
            sortPeriod(a.original.validPeriod, b.original.validPeriod),
          filterFn: 'validPeriod',
        },
      ),
    ],
    [t],
  );

  const [{ validationRuleId, validationRuleIndex }] = usePanelParams();
  const validationRule = useMemo(
    () =>
      validationRuleId
        ? validationRules.find((rule) => rule.id === Number(validationRuleId))
        : validationRules[Number(validationRuleIndex)],
    [validationRules, validationRuleId, validationRuleIndex],
  );

  const [, setPanelParams] = usePanelParams();

  return (
    <ValidationTableWrapper>
      <Table
        defaultSort={[{ id: 'code', desc: false }]}
        name="validationRules"
        filters={columnFilters}
        data={validationRules}
        columns={columns}
        onFilter={setColumnFilters}
        options={options}
        rowKey="code"
        sticky
        virtual
        scrollContainerSelector="#detail-layout-main-content"
      />
      {validationRule && (
        <ValidationRuleViewPanel
          onClose={() => setPanelParams()}
          validationRule={validationRule}
        />
      )}
    </ValidationTableWrapper>
  );
}

const CodeCell: FC<CellContext<ValidationRuleValidDataType, any>> = memo(
  (props) => {
    const [, setPanelParams] = usePanelParams();
    const validationRuleId = props.row.original.id;

    return (
      <TextLink
        id={`validationRules-${props.row.index}-code`}
        href={useHrefWithQueryParams({ validationRuleId })}
        onClick={suppressHref(() => setPanelParams({ validationRuleId }))}
      >
        {props.renderValue()}
      </TextLink>
    );
  },
);
